import { YMaps, Map } from "@pbe/react-yandex-maps";
import styles from "./styles.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { IPolygonTransform } from "./chooseMapPolygon";

export type MapItem = {
  id: number;
  selected: boolean;
  coords: [number, number];
};

type ItemsYmapProps = {
  onPolygonClick: (polygonIds: number[]) => void;
  onLockRenderMap: () => void;
  zoom?: number;
  transformedPolygonItems: IPolygonTransform[];
  selectedPolygonIds: number[];
  isRerenderMap: boolean;
};

const KRSK_COORDS = [92.874172, 56.023097];
const DEFAULT_ZOOM = 11;
const MAX_SELECTED_COUNT = 3;

// доп опции к полигону для лейбла
const LABEL_OPTIONS = {
  // Стандартный вид текста будет темный с белой обводкой.
  labelDefaults: "dark",
  // Макет подписи.
  labelLayout: "<div>{{properties.hintContent}}</div>",
  // Цвет заливки.
  fillColor: "rgba(27, 125, 190, 0.7)",
  // Цвет обводки.
  strokeColor: "rgba(255, 255, 255, 0.8)",
  // Отключим показ всплывающей подсказки при наведении на полигон.
  openHintOnHover: false,
  // Размер текста подписей зависит от масштаба.
  // На уровнях зума 3-6 размер текста равен 12, а на уровнях зума 7-18 равен 14.
  labelTextSize: { "3_6": 12, "7_18": 14 },
  cursor: "grab",
  labelDotCursor: "pointer",
  // Допустимая погрешность в расчете вместимости подписи в полигон.
  labelPermissibleInaccuracyOfVisibility: 4,
};

const PolygonItemsYmap: React.FC<ItemsYmapProps> = (props) => {
  const {
    transformedPolygonItems,
    selectedPolygonIds,
    zoom,
    onPolygonClick,
    onLockRenderMap,
  } = props;

  useEffect(() => {
    if (transformedPolygonItems.length) {
      onLockRenderMap();
    }
  }, [transformedPolygonItems]);

  function getCenter(coords: { lat: number; lon: number }[]): number[] {
    if (!coords || coords.length === 0) {
      return [0, 0];
    }
    let latSum = 0;
    let lonSum = 0;
    for (const coord of coords) {
      latSum += coord.lat;
      lonSum += coord.lon;
    }

    const numCoords = coords.length;
    return [lonSum / numCoords, latSum / numCoords];
  }

  const mapRef = useRef<any>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);

  const loadJS = (id: string, url: string, location: any, onLoad?: any) => {
    const scriptTag = document.createElement("script");
    location.appendChild(scriptTag);
    scriptTag.src = url;
    scriptTag.id = id;
    if (onLoad) {
      scriptTag.onload = onLoad;
    }
  };

  const getLabelsModule = (ymaps: any) => {
    loadJS(
      "calculateArea",
      "https://yastatic.net/s3/mapsapi-jslibs/area/0.0.1/util.calculateArea.min.js",
      document.head,
      () => {
        loadJS(
          "polylabel",
          "https://yastatic.net/s3/mapsapi-jslibs/polylabeler/1.0.2/polylabel.min.js",
          document.head,
          () => getReadyModuleLabels(ymaps)
        );
      }
    );
  };

  const getReadyModuleLabels = (ymaps: any) => {
    ymaps
      .ready(["polylabel.create"])
      .then(() => {
        setLoaded(true);
      })
      .catch((err: any) => console.log(err));
  };

  const getPlaceMarkData = (
    polygonItem: IPolygonTransform,
    content: number
  ) => {
    return {
      type: "Feature",
      geometry: {
        type: "Point", // Меняем тип на Point для метки
        coordinates: getCenter(polygonItem.coordinates), // Координаты метки
      },
      options: {
        preset: "islands#blueCircleIcon",
      },
      properties: {
        iconContent: content,
      },
      id: `${polygonItem.id}-placemark`,
    };
  };

  const getPolygonData = (polygonItem: IPolygonTransform) => {
    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [
          polygonItem.coordinates.map((coord) => [coord.lon, coord.lat]),
        ],
      },
      options: {
        fillColor: polygonItem.color,
        strokeColor: "#0000FF",
        opacity: polygonItem.isPlaceMarck ? 0.85 : 0.5,
        strokeWidth: polygonItem.isPlaceMarck ? 2 : 1,
        strokeOpacity: polygonItem.isPlaceMarck ? 1 : 0.6,
        strokeStyle: "shortdash",
      },
      properties: {
        hintContent: polygonItem.name,
        iso3166: "RU-KRY",
        level: "4",
        name: polygonItem.name,
      },
      id: polygonItem.id,
    };
  };

  const getPolygonsData = () => {
    let features = [];

    for (const polygonItem of transformedPolygonItems) {
      features.push(getPolygonData(polygonItem));
      if (polygonItem.isPlaceMarck) {
        features.push(getPlaceMarkData(polygonItem, polygonItem?.content ?? 0));
      }
    }

    return {
      type: "FeatureCollection",
      features: features,
    };
  };

  useEffect(() => {
    if (transformedPolygonItems && mapInstance && loaded && mapRef.current) {
      let selectedIds: number[] = selectedPolygonIds;
      const objectManager = new mapInstance.ObjectManager();

      const polygons = getPolygonsData().features.map(function (feature) {
        feature.options = { ...LABEL_OPTIONS, ...feature.options };
        return feature;
      });

      objectManager.add(polygons);

      mapRef.current.geoObjects.add(objectManager);
      // подписи
      new mapInstance.polylabel.create(mapRef.current, objectManager);

      const removeGeoObjects = () => {
        objectManager.removeAll();
      };

      const addPlaceMark = (withMove?: boolean) => {
        let selectedPolygons = transformedPolygonItems.filter((polygon) => {
          return selectedIds.includes(Number(polygon.id));
        });

        const moveLastClickPolygonToEnd = () => {
          const lastSelectedPolygons = selectedPolygons.find(
            (polygon) => polygon.id === selectedIds[selectedIds.length - 1]
          );

          selectedPolygons = selectedPolygons.filter(
            (polygon) => polygon.id !== lastSelectedPolygons?.id
          );

          if (lastSelectedPolygons) {
            selectedPolygons.push(lastSelectedPolygons);
          }
        };

        if (withMove) {
          moveLastClickPolygonToEnd();
        }

        selectedPolygons?.forEach((polygon) => {
          removePlaceMark(polygon);
        });

        selectedPolygons?.forEach((polygon: any, index: number) => {
          objectManager.add(getPlaceMarkData(polygon, index + 1));
        });
      };

      const removePlaceMark = function (
        polygonItem: IPolygonTransform,
        recalculatePlaceMarks?: boolean
      ) {
        objectManager.remove([`${polygonItem.id}-placemark`]);
        if (recalculatePlaceMarks) {
          addPlaceMark();
        }
      };

      const mutatedPolygon = (
        polygonItem: IPolygonTransform,
        type: "add" | "rem"
      ) => {
        const options = {
          fillColor: polygonItem.color,
          strokeColor: "#0000FF",
          opacity: type === "add" ? 0.85 : 0.5,
          strokeWidth: type === "add" ? 2 : 1,
          strokeOpacity: type === "add" ? 1 : 0.6,
          strokeStyle: "shortdash",
        };

        objectManager.remove([polygonItem.id]);

        const newPolygon = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [
              polygonItem.coordinates.map((coord) => [coord.lon, coord.lat]),
            ],
          },
          options,
          properties: {
            hintContent: polygonItem.name,
            iso3166: "RU-KRY",
            level: "4",
            name: polygonItem.name,
          },
          id: polygonItem.id,
        };

        /// возможно выделение без стилей выглядит лучше.
        objectManager.add({
          ...newPolygon,
          options: { ...LABEL_OPTIONS, ...newPolygon.options },
        });

        mapRef.current.geoObjects.add(objectManager);
      };

      objectManager.events.add(["click", "labelclick"], (event: any) => {
        // id  у нас для всех един, только есть приствки. Поэтому что бы их убрать выполним следующие операции.
        const eventObjectId = event.get("objectId");
        const clearStringId = String(eventObjectId).replace(/[^0-9]/g, "");
        const id = Number(clearStringId);

        if (id) {
          const item = transformedPolygonItems.find((item) => item.id === id);

          if (item && selectedIds.includes(id)) {
            selectedIds = selectedIds.filter((selectedId) => selectedId !== id);

            removePlaceMark(item, true);
            mutatedPolygon(item, "rem");
            onPolygonClick(selectedIds);
            return;
          }

          if (item && selectedIds.length !== MAX_SELECTED_COUNT) {
            selectedIds.push(id);
            addPlaceMark(true);
            mutatedPolygon(item, "add");
            onPolygonClick(selectedIds);
          }
        }
      });

      return () => {
        removeGeoObjects();
      };
    }
  }, [loaded, mapInstance, transformedPolygonItems]);

  useEffect(() => {
    if (mapInstance && !loaded) {
      getLabelsModule(mapInstance);
    }
  }, [mapInstance]);

  useEffect(() => {
    return () => {
      if (window.ymaps) {
        //@ts-ignore
        delete window.ymaps;
      }
    };
  }, []);

  if (!transformedPolygonItems.length) {
    return <></>;
  }

  return (
    <YMaps
      query={{
        load: "package.full",
        coordorder: "longlat",
        ns: "ymaps",
      }}
    >
      <Map
        modules={["geoQuery", "meta", "borders", "ObjectManager"]}
        className={styles.yandexMap}
        state={{
          center: KRSK_COORDS,
          zoom: zoom ? zoom : DEFAULT_ZOOM,
          controls: ["fullscreenControl"],
          behaviors: [
            "drag",
            "scrollZoom",
            "dblClickZoom",
            "rightMouseButtonMagnifier",
            "multiTouch",
          ],
        }}
        instanceRef={mapRef}
        onLoad={(ymap) => {
          setMapInstance(ymap);
        }}
      />
    </YMaps>
  );
};

export default React.memo(
  PolygonItemsYmap,
  (prev, current) => prev.isRerenderMap !== current.isRerenderMap
);
